/**
 * Shared presentation components for bank balance grouping.
 *
 * Used by both DashboardPage (bank card) and BankBalancesSection (dedicated page)
 * so both surfaces render identically and stay consistent.
 *
 * Layout per row:
 *   name (with sublabel) | currency pill | native amount (bold primary)
 *                                         converted amount (muted, suppressed
 *                                         when native === display currency)
 *                                         timestamp (muted)
 *
 * Group header: logo, bank name, account count, per-bank total (display currency),
 * collapse chevron. Clicking toggles row visibility (local state, no refetch).
 *
 * Entity assignment: when entities + onAssignEntity are provided, each row
 * shows a dropdown to assign the account to an entity. Both entity_id and
 * entity_uuid are written together by the backend (PATCH /admin/bank-accounts/
 * {id}/assign), so a frontend-only entity_id write cannot cause divergence.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { BankLogo } from './BankLogo';
import { formatCurrency } from '../lib/chartFormatters';
import { parseBalance } from '../lib/bankBalancesGrouping';
import { convertAmount } from '../lib/currencyConvert';

/** Format an ISO date string as a short human-readable timestamp. */
function formatAsOf(isoStr) {
  if (!isoStr) return 'Unknown';
  try {
    var d = new Date(isoStr);
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

/**
 * Inline entity assignment dropdown for a bank account row.
 *
 * Shows current entity (or "Unassigned") and allows selection from the
 * canonical entity list. On change, calls onAssignEntity(accountId, entityUuid).
 */
function EntityAssignSelect({ account, entities, onAssignEntity, assigning }) {
  // Resolve current entity UUID: prefer entity_uuid, fall back to matching
  // entity_id (type code) against the entity list. Accounts that predate
  // entity_uuid backfill have entity_id set but no entity_uuid.
  var currentUuid = account.entity_uuid || '';
  if (!currentUuid && account.entity_id && account.entity_id !== 'unassigned') {
    for (var i = 0; i < entities.length; i++) {
      if (entities[i].type === account.entity_id) {
        currentUuid = entities[i].id;
        break;
      }
    }
  }
  var isUnassigned = !currentUuid;

  function handleChange(e) {
    var uuid = e.target.value;
    if (uuid === currentUuid) return;
    onAssignEntity(account.id, uuid);
  }

  return (
    <select
      className={
        'text-xs border rounded px-1.5 py-0.5 max-w-[140px] truncate bg-background ' +
        (isUnassigned ? 'border-amber-400 text-amber-700' : 'border-border text-muted-foreground')
      }
      value={currentUuid}
      onChange={handleChange}
      disabled={assigning}
      data-testid="entity-assign-select"
      title={isUnassigned ? 'Assign this account to an entity' : 'Change entity assignment'}
    >
      <option value="">Unassigned</option>
      {entities.map(function (ent) {
        return <option key={ent.id} value={ent.id}>{ent.name}</option>;
      })}
    </select>
  );
}

/**
 * Single account row inside a bank group.
 *
 * Columns: name + sublabel, currency pill, amount block.
 * Amount block: native balance (bold primary), converted balance (muted,
 * suppressed when account currency equals display currency), timestamp.
 *
 * Optional: entities + onAssignEntity props enable per-row entity assignment.
 */
export function AccountRow({ account, displayCurrency, fxRates, entities, onAssignEntity, assigning, onAccountClick }) {
  var bal = parseBalance(account.balance);
  var converted = bal != null
    ? convertAmount(bal, account.currency, displayCurrency, fxRates)
    : null;
  var showConverted = account.currency !== displayCurrency && bal != null && converted != null;
  var showAssign = entities && entities.length > 0 && onAssignEntity;
  var isClickable = typeof onAccountClick === 'function';

  function handleClick() {
    if (isClickable) onAccountClick(account);
  }

  return (
    <div
      className={'flex items-center gap-3 py-2 px-3 pl-12 hover:bg-muted/30 transition-colors' + (isClickable ? ' cursor-pointer' : '')}
      data-testid="bank-balance-row"
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{account.account_name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-xs text-muted-foreground">{account.bank_name}</p>
          {showAssign && (
            <EntityAssignSelect
              account={account}
              entities={entities}
              onAssignEntity={onAssignEntity}
              assigning={assigning}
            />
          )}
        </div>
      </div>
      <Badge variant="outline" className="font-mono text-[10px] shrink-0 px-1.5 py-0">
        {account.currency}
      </Badge>
      <div className="text-right shrink-0">
        <p className={'text-sm font-mono font-semibold' + (bal == null ? ' text-muted-foreground' : '')} data-testid="account-balance-native">
          {bal != null ? formatCurrency(bal, account.currency) : '--'}
        </p>
        {showConverted && (
          <p className="text-xs text-muted-foreground font-mono" data-testid="account-balance-converted">
            {formatCurrency(converted, displayCurrency)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {formatAsOf(account.balance_as_of)}
        </p>
      </div>
    </div>
  );
}

/**
 * Collapsible bank group: header row with logo, name, account count, total,
 * and a chevron. Child AccountRow components shown/hidden by local state.
 *
 * @param {string}  bankName         Bank display name
 * @param {Array}   accounts         Account records in this group
 * @param {number}  subtotal         Pre-computed subtotal in display currency
 * @param {string}  displayCurrency  Active display currency (GBP/EUR)
 * @param {object}  fxRates          FX rates map for conversion
 * @param {boolean} defaultExpanded  Initial expanded state (default true)
 * @param {boolean} subtotalMuted   Render subtotal as subordinate (muted, lighter weight)
 * @param {Array}   entities         Optional entity list for assignment dropdown
 * @param {Function} onAssignEntity  Optional callback for entity assignment
 * @param {boolean} assigning        Optional flag while assignment in flight
 */
export function BankGroup({ bankName, accounts, subtotal, displayCurrency, fxRates, defaultExpanded, entities, onAssignEntity, assigning, subtotalMuted, onAccountClick }) {
  var [expanded, setExpanded] = useState(defaultExpanded !== false);

  return (
    <div data-testid={'bank-group-' + bankName}>
      <button
        onClick={function () { setExpanded(function (e) { return !e; }); }}
        className="flex items-center gap-3 w-full text-left py-2.5 px-3 hover:bg-muted/40 transition-colors"
        data-testid="bank-group-header"
      >
        <BankLogo bankName={bankName} size="sm" />
        <span className="text-sm font-semibold flex-1">{bankName}</span>
        <span className="text-xs text-muted-foreground mr-1">
          {accounts.length} acct{accounts.length !== 1 ? 's' : ''}
        </span>
        <span className={"text-sm font-mono whitespace-nowrap" + (subtotalMuted ? " font-medium text-muted-foreground" : " font-bold")} data-testid="bank-group-subtotal">
          {formatCurrency(subtotal, displayCurrency)}
        </span>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {expanded && (
        <div className="border-t border-border/40 bg-muted/20" data-testid="bank-group-rows">
          {accounts.map(function (acc) {
            return (
              <AccountRow
                key={acc.id}
                account={acc}
                displayCurrency={displayCurrency}
                fxRates={fxRates}
                entities={entities}
                onAssignEntity={onAssignEntity}
                assigning={assigning}
                onAccountClick={onAccountClick}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
